import * as React from "react";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";

import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import axios from "axios";

import { Button, Form, Loader, Modal, TextArea, TextAreaProps } from "semantic-ui-react";

import { getFeatureToggles } from "@teamprevent/common/getFeatureToggles";

import * as FeatureToggleNames from "@teamprevent/common/featureToggleNames";

import { toast } from "@teamprevent/common/toasts";

import { CUSTOMER_ROLES } from "@teamprevent/common/auth/roles";

import { routeIdSelector as customerIdSelector } from "../../crud/customer/selectors";

import { savePpqClinicalNotes, submitPpqClinicalNotesAmendments } from "../actions";
import { ApplicationState } from "../../applicationState";
import { AddTaskFormModel } from "../../task/model";

import { LineManagersApi } from "../../lineManagers/LineManagersApi";

import { HrRepresentativesApi } from "../../hrRepresentatives/HrRepresentativesApi";

import { taskSelector } from "../../task/selectors";

import { PpqClinicalNotesSections } from "./PpqClinicalNotesSections";

import { ClinicalNotes, questionnaireKey as clinicalNotesQuestionnaireKey } from "./ClinicalNotes";

const lineManagersApi = new LineManagersApi(axios);
const hrRepresentativesApi = new HrRepresentativesApi(axios);

export const outcomeQuestionId = "Outcome";

interface SubmitPpqClinicalNotesAmendmentsModel {
    ohCaseId: string;
    taskId: string;
    questions: AnsweredQuestionWithId[];
}

export const AmendPpqClinicalNotes: React.FunctionComponent = () => {
    const task = useSelector(taskSelector);

    const customerId = useSelector((state: ApplicationState) => customerIdSelector(state));
    const featureToggles = getFeatureToggles();
    const enablePPQConsentLoop = featureToggles[FeatureToggleNames.EnablePPQConsentLoop];
    const preventPPQClinicalNotesDuplicateSubmission = featureToggles[FeatureToggleNames.PreventPPQClinicalNotesDuplicateSubmission];

    const dispatch = useDispatch();
    const [tasks, setTasks] = React.useState<AddTaskFormModel[]>([]);
    const [submitted, setSubmitted] = React.useState(false);
    const [managerOptions, setManagerOptions] = React.useState<{ id: string; name: string; email: string }[]>([]);
    const [hrUserOptions, setHrUserOptions] = React.useState<{ id: string; name: string; email: string }[]>([]);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [reasonForEdit, setReasonForEdit] = React.useState<string>("");
    const [submitModel, setSubmitModel] = React.useState<SubmitPpqClinicalNotesAmendmentsModel | undefined>(undefined);

    React.useEffect(() => {
        async function fetchManagers() {
            setIsLoading(true);
            const [managers, hrUsers] = await Promise.all([
                lineManagersApi.getAll(customerId, CUSTOMER_ROLES.RECRUITMENT_MANAGER.name),
                hrRepresentativesApi.getAll(customerId)
            ]);

            setManagerOptions(managers.map(m => ({ id: m.id, name: m.name, email: m.email })));
            setHrUserOptions(hrUsers.map(m => ({ id: m.id, name: m.name, email: m.email })));
            setIsLoading(false);
        }

        if (enablePPQConsentLoop) {
            fetchManagers();
        }
    }, [customerId, enablePPQConsentLoop]);

    const preSubmitQuestionnaireModal = React.useCallback((ohCaseId: string, taskId: string, _customerId: string, questions: AnsweredQuestionWithId[]) => {
        if (preventPPQClinicalNotesDuplicateSubmission && submitted) {
            toast.error("The form has already been submitted");
            return;
        }
        setSubmitModel({ ohCaseId, taskId, questions });
        setIsModalOpen(true);
    }, [preventPPQClinicalNotesDuplicateSubmission, submitted]);

    const submitQuestionnaire = React.useCallback((ohCaseId: string, taskId: string, questions: AnsweredQuestionWithId[]) => {
        setSubmitted(true);
        dispatch(submitPpqClinicalNotesAmendments(ohCaseId, taskId, questions, clinicalNotesQuestionnaireKey, tasks, reasonForEdit));
    }, [dispatch, reasonForEdit, tasks]);

    const saveQuestionnaire = React.useCallback((ohCaseId: string, taskId: string, _customerId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        dispatch(savePpqClinicalNotes(ohCaseId, taskId, customerId, questions, eTag));
    }, [customerId, dispatch]);

    const tasksChanged = React.useCallback((updatedTasks: { task: AddTaskFormModel; isValid: boolean }[]) => {
        setTasks(updatedTasks.filter(updatedTask => updatedTask.isValid).map(t => t.task) );
    }, []);

    const onReasonForEditChange = React.useCallback((_event: React.FormEvent<HTMLTextAreaElement>, data: TextAreaProps) => {
        setReasonForEdit(data.value?.toString() ?? "");
    }, []);

    const onClose = React.useCallback(() => {
        setReasonForEdit("");
        setIsModalOpen(false);
    }, []);

    const onModalSubmit = React.useCallback(() => {
        if (!reasonForEdit)
        {
            toast.error("Please provide a reason for the amendment");
            return;
        }
        if (submitModel) {
            setIsModalOpen(false);
            submitQuestionnaire(submitModel.ohCaseId, submitModel.taskId, submitModel.questions);
        }
    }, [reasonForEdit, submitModel, submitQuestionnaire]);

    const intro = React.useMemo(() => {
        return `Requested Changes: ${task?.notes}`;
    }, [task]);

    if (isLoading || !task)
    {
        return <Loader active />;
    }

    return (
        <>
            <ClinicalNotes
                intro={intro}
                saveQuestionnaire={saveQuestionnaire}
                submitQuestionnaire={preSubmitQuestionnaireModal}
                hideDidNotAttendButton={true}
            >
                <PpqClinicalNotesSections
                    onTasksChange={tasksChanged}
                    showErrors={submitted}
                    hrRepresentatives={hrUserOptions}
                    lineManagers={managerOptions}
                    showConsentSection={false} />
            </ClinicalNotes>
            <Modal size="small" open={isModalOpen}>
                <Modal.Header>
                    Reasons for Certificate Amendment
                </Modal.Header>
                <Modal.Content>
                    <Form>
                        <Form.Field>
                            <TextArea onChange={onReasonForEditChange} />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={onModalSubmit}
                        disabled={submitted}
                        loading={submitted}
                    >
                        Submit
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};
